import React, { useState, useMemo } from "react";
import { X, Upload, ChevronRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";

// Reusable 3-step CSV import modal.
// Steps: upload → map columns → confirm/results.
// Props:
//   resource: 'people' | 'projects' | 'tasks'
//   context: { folder_id?, project_id? }   (required for projects/tasks)
//   contextOptions: { folders?: [...], projects?: [...] } to render a picker for context
//   onClose, onSuccess (called with {inserted, skipped})
export function CsvImportModal({ resource, context = {}, contextOptions = {}, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null); // { columns, sample_rows, target_fields, suggested_mapping, row_count }
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [ctx, setCtx] = useState(context);

  const label = resource === "people" ? "People" : resource === "projects" ? "Projects" : "Tasks";

  const readFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(e.target.result);
    reader.readAsText(file);
  };

  const goPreview = async () => {
    if (!csvText.trim()) return;
    setBusy(true);
    try {
      const p = await endpoints.importsPreview(resource, csvText);
      setPreview(p);
      setMapping(p.suggested_mapping || {});
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read CSV"); }
    finally { setBusy(false); }
  };

  const canRun = useMemo(() => {
    if (!preview) return false;
    // All required fields must have a mapping
    const req = preview.target_fields.filter((f) => f.required);
    return req.every((f) => mapping[f.key]);
  }, [preview, mapping]);

  const execute = async () => {
    setBusy(true);
    try {
      const r = await endpoints.importsExecute(resource, csvText, mapping, ctx);
      setResult(r);
      setStep(3);
      toast.success(`${r.inserted} ${label.toLowerCase()} imported`);
      onSuccess?.(r);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="fos-card p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`csv-import-modal-${resource}`}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold">Import {label} from CSV</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <StepStrip step={step} />

        {step === 1 && (
          <div className="mt-4 space-y-3">
            <label className="fos-card p-6 flex flex-col items-center justify-center gap-2 border-dashed cursor-pointer hover:bg-neutral-50" data-testid="csv-dropzone">
              <Upload size={20} className="text-neutral-500" />
              <p className="text-sm text-neutral-700">{fileName || "Click to choose a CSV file"}</p>
              <p className="text-xs text-neutral-500">The first row must be a header</p>
              <input type="file" accept=".csv,text/csv" onChange={(e) => readFile(e.target.files[0])} className="hidden" data-testid="csv-file-input" />
            </label>
            <details className="text-xs">
              <summary className="cursor-pointer text-neutral-500">Or paste CSV text</summary>
              <textarea
                value={csvText}
                onChange={(e) => { setCsvText(e.target.value); setFileName(e.target.value ? "pasted" : ""); }}
                rows={5}
                placeholder="name,email,role
Elena Torres,elena@acme.com,VP Engineering"
                className="mt-2 w-full text-xs px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none resize-none focus:border-neutral-500 font-mono"
                data-testid="csv-textarea"
              />
            </details>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-neutral-200">
              <button onClick={onClose} className="text-sm px-3 py-2 rounded-sm border border-neutral-200 text-neutral-600">Cancel</button>
              <button
                onClick={goPreview}
                disabled={busy || !csvText.trim()}
                data-testid="csv-preview-btn"
                className="text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white disabled:opacity-40 flex items-center gap-1.5"
              >
                {busy ? "Reading…" : <>Next<ChevronRight size={12} /></>}
              </button>
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div className="mt-4 space-y-4">
            <div className="text-xs text-neutral-500">
              Detected <span className="text-neutral-900">{preview.columns.length}</span> columns and <span className="text-neutral-900">{preview.row_count}</span> rows.
              Map each target field to a CSV column below.
            </div>

            {/* Context picker */}
            {resource === "projects" && contextOptions.folders && (
              <ContextField label="Folder to import into" value={ctx.folder_id} onChange={(v) => setCtx({ ...ctx, folder_id: v })} options={contextOptions.folders.map((f) => ({ id: f.id, label: f.name }))} testid="csv-context-folder" required />
            )}
            {resource === "tasks" && contextOptions.projects && (
              <ContextField label="Project to import tasks into" value={ctx.project_id} onChange={(v) => setCtx({ ...ctx, project_id: v })} options={contextOptions.projects.map((p) => ({ id: p.id, label: p.name }))} testid="csv-context-project" required />
            )}

            <div className="space-y-2">
              {preview.target_fields.map((f) => (
                <div key={f.key} className="flex items-center gap-3">
                  <label className="text-xs text-neutral-700 w-1/3 shrink-0">
                    {f.label}{f.required && <span className="text-[#FF453A] ml-0.5">*</span>}
                  </label>
                  <select
                    value={mapping[f.key] || ""}
                    onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}
                    className="flex-1 text-sm px-3 py-1.5 rounded-sm border border-neutral-200 bg-white outline-none"
                    data-testid={`csv-map-${f.key}`}
                  >
                    <option value="">— skip —</option>
                    {preview.columns.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </div>
              ))}
            </div>

            {preview.sample_rows.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-neutral-500">Preview first {preview.sample_rows.length} row(s)</summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead><tr>{preview.columns.map((c) => <th key={c} className="text-left px-2 py-1 text-neutral-500 font-medium border-b border-neutral-200">{c}</th>)}</tr></thead>
                    <tbody>{preview.sample_rows.map((row, i) => (
                      <tr key={i}>{preview.columns.map((c) => <td key={c} className="px-2 py-1 border-b border-neutral-100 text-neutral-800">{row[c] || ""}</td>)}</tr>
                    ))}</tbody>
                  </table>
                </div>
              </details>
            )}

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-neutral-200">
              <button onClick={() => setStep(1)} className="text-sm px-3 py-2 rounded-sm border border-neutral-200 text-neutral-600">Back</button>
              <button
                onClick={execute}
                disabled={busy || !canRun || (resource === "projects" && !ctx.folder_id) || (resource === "tasks" && !ctx.project_id)}
                data-testid="csv-execute-btn"
                className="text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white disabled:opacity-40 flex items-center gap-1.5"
              >
                {busy ? "Importing…" : `Import ${preview.row_count} rows`}
              </button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="mt-6 text-center space-y-4" data-testid="csv-result">
            <CheckCircle2 size={28} className="mx-auto text-neutral-900" />
            <div>
              <p className="text-lg font-semibold">Imported {result.inserted} {label.toLowerCase()}</p>
              {result.skipped > 0 && (
                <p className="text-xs text-neutral-500 mt-1">
                  <AlertTriangle size={11} className="inline -mt-0.5 mr-1 text-[#F97316]" />
                  Skipped {result.skipped} row(s) with validation errors.
                </p>
              )}
            </div>
            {result.errors?.length > 0 && (
              <details className="text-left text-xs">
                <summary className="cursor-pointer text-neutral-500">See row errors</summary>
                <ul className="mt-2 space-y-1">
                  {result.errors.map((e, i) => (<li key={i} className="text-red-600">Row {e.row}: {e.error}</li>))}
                </ul>
              </details>
            )}
            <button onClick={onClose} className="text-sm px-4 py-2 rounded-sm bg-neutral-900 text-white" data-testid="csv-done-btn">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function StepStrip({ step }) {
  return (
    <div className="flex items-center gap-2 mt-3">
      {["Upload", "Map columns", "Import"].map((label, i) => {
        const idx = i + 1;
        return (
          <React.Fragment key={label}>
            <div className={`h-5 min-w-5 px-1.5 rounded-full text-[10px] font-medium flex items-center justify-center ${idx === step ? "bg-neutral-900 text-white" : idx < step ? "bg-neutral-300 text-neutral-800" : "bg-neutral-100 text-neutral-500"}`}>{idx}</div>
            <span className={`text-xs ${idx === step ? "text-neutral-900 font-medium" : "text-neutral-500"}`}>{label}</span>
            {idx < 3 && <div className="flex-1 h-px bg-neutral-200 mx-1" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ContextField({ label, value, onChange, options, testid, required }) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-xs text-neutral-700 w-1/3 shrink-0">
        {label}{required && <span className="text-[#FF453A] ml-0.5">*</span>}
      </label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 text-sm px-3 py-1.5 rounded-sm border border-neutral-200 bg-white outline-none" data-testid={testid}>
        <option value="">— choose —</option>
        {options.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
      </select>
    </div>
  );
}
